/**
 * useTenantPageTitle — vibe-designer 1.421.0
 *
 * Sets document.title to "<tenant> — Vibe Designer" once /api/me resolves
 * the tenant id (tenantStore). Every tenant previously served the same
 * static index.html <title>, so bookmarks for two tenants' Vibe Designers
 * were indistinguishable. Tenant-first ordering keeps truncated bookmark
 * labels unique. While the tenant is unknown (pre-hydrate, auth-off local
 * dev) the static index.html title is left untouched.
 */
import { useEffect } from 'react';
import { useTenantStore } from '@/stores/tenantStore';
import { EVENSTAR_DISPLAY_NAME, getBrand } from '@/config/brand';

/** Must match frontend/index.html <title> for the gamemagick default brand. */
export const DEFAULT_PAGE_TITLE = 'Vibe Designer - GameMagick AI';

export function useTenantPageTitle(): void {
  const tenantId = useTenantStore((s) => s.tenantId);
  useEffect(() => {
    if (tenantId) {
      // Whitelabel Phase B: brand-aware product name (gamemagick default keeps
      // the historical "<tenant> — Vibe Designer" title byte-identical).
      document.title = `${tenantId} — ${getBrand().productNameVibe}`;
    } else if (getBrand().displayName === EVENSTAR_DISPLAY_NAME) {
      // Evenstar SSO-login branding (2026-08-04): the static index.html
      // <title> says "GameMagick AI" for the whole unauthenticated login view
      // — a leak on the public Evenstar apex. Fires ONLY when the brand rail
      // resolved to the evenstar override, so gamemagick + dojo pre-auth
      // titles stay byte-identical to before.
      document.title = `Vibe Designer - ${EVENSTAR_DISPLAY_NAME}`;
    }
  }, [tenantId]);
}
